import React, { useCallback, useMemo, useState } from "react";

import { Icon } from "@aws-amplify/ui-react";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/routes";

import { DashboardFooter } from "@pages/dashboard/DashboardFooter";
import { DashboardHeader } from "@pages/dashboard/DashboardHeader";
import { RegisterPageOutline } from "@pages/register/RegisterPageOutline";

import styles from "./CustomizedSignIn.module.scss";

const EyeIconSvg = () => {
  return (
    <>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    </>
  );
};

const EyeIconDisabledSvg = () => {
  return (
    <>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </>
  );
};

const WarningIcon = () => {
  return (
    <Icon
      ariaLabel="Error"
      pathData="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
      color="currentColor"
      height={24}
      width={24}
    />
  );
};

const DismissIcon = () => {
  return (
    <svg aria-label="Error" viewBox="0 0 24 24">
      <path
        d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
        fill="currentColor"
      ></path>
    </svg>
  );
};

const VERIFICATION_CODE_EXPIRED = [
  "TOTP has expired",
  "ExpiredCodeException",
  "Time-based OneTime Password (TOTP) has expired",
];

const VERIFICATION_CODE_MISMATCH = ["Code mismatch", "CodeMismatchException", "Invalid code provided"];

const SESSION_ERRORS = ["Session expired", "Invalid session"];

const NETWORK_ERRORS = ["Network error", "Failed to fetch"];

interface ErrorDisplayProps {
  error: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, dismissible = true, onDismiss }) => {
  const errorToDisplay = useMemo(() => {
    // Authentication Errors
    if (error.includes("Incorrect username or password")) {
      return "The email or password you entered is incorrect";
    }
    if (error.includes("User does not exist")) {
      return "We couldn't find an account with that email";
    }
    if (error.includes("Password attempts exceeded")) {
      return "Too many failed attempts. Please try again later";
    }
    if (error.includes("User is not confirmed")) {
      return "Please verify your email before signing in";
    }

    // Verification Code Errors
    if (VERIFICATION_CODE_EXPIRED.some((message) => error.includes(message))) {
      return "The verification code has expired. Please request a new one";
    }
    if (VERIFICATION_CODE_MISMATCH.some((message) => error.includes(message))) {
      return "Invalid verification code. Please try again or request a new code";
    }

    // Session Errors
    if (SESSION_ERRORS.some((message) => error.includes(message))) {
      return "Your session has expired. Please sign in again";
    }

    // Network Errors
    if (NETWORK_ERRORS.some((message) => error.includes(message))) {
      return "Unable to connect. Please check your internet connection";
    }

    // Generic AWS Cognito Errors
    if (error.includes("NotAuthorizedException")) {
      return "Authentication failed. Please check your credentials";
    }
    if (error.includes("LimitExceededException")) {
      return "Too many attempts. Please try again later";
    }
    if (error.includes("UserNotFoundException")) {
      return "We couldn't find an account with that email";
    }
    return error;
  }, [error]);

  if (!error) {
    return <></>;
  }

  return (
    <div className="amplify-alert amplify-alert--error" role="alert">
      <div className="amplify-alert__body">
        <div className="amplify-alert__content amplify-flex">
          <span className={styles["error_icon"]}>
            <WarningIcon />
          </span>
          <span className={classNames("amplify-alert__message", styles["error_message"])}>{errorToDisplay}</span>
          {dismissible && (
            <button
              className={classNames(
                styles["dismiss_error_button"],
                "amplify-button amplify-field-group__control amplify-button--link amplify-alert__dismiss",
              )}
              onClick={onDismiss}
              aria-label="Close alert"
            >
              <span className="amplify-icon">
                <DismissIcon />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const CustomizedSignIn = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleSignIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError("");

      try {
        await signIn({
          username,
          password,
        });
        setShowOTP(true); // Show OTP input after successful sign-in
      } catch (err) {
        setError(`Error signing in: ${err}`);
        console.error("Error signing in:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [password, username],
  );

  const handleResendCode = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError("");

      try {
        const keyPrefix = "CognitoIdentityServiceProvider";
        Object.keys(localStorage)
          .filter((key) => key.startsWith(keyPrefix))
          .forEach((key) => localStorage.removeItem(key));

        await signIn({
          username,
          password,
        });
      } catch (err) {
        setError("Error signing in. Please check your credentials.");
        console.error("Error signing in:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [password, username],
  );

  const handleConfirmSignIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError("");

      try {
        await confirmSignIn({
          challengeResponse: otp,
        });
      } catch (err) {
        setError("An error occurred during verification.");
        console.error("Error confirming sign in:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [otp],
  );

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  }, []);

  const handleOtpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
  }, []);

  const handleForgotPassword = useCallback(() => {
    navigate(ROUTES.ForgotPassword.path);
  }, [navigate]);

  if (!showOTP) {
    return (
      <RegisterPageOutline showLogIn={false}>
        <div data-amplify-authenticator="">
          <div data-amplify-container="">
            <div data-amplify-router="">
              <div data-amplify-router-content="" className="amplify-tabs__panel amplify-tabs__panel--active">
                <form onSubmit={handleSignIn} data-amplify-form="">
                  <div className="amplify-flex flex-column">
                    <fieldset className="flex-column amplify-flex">
                      <div className="amplify-flex amplify-field amplify-textfield">
                        <label className="amplify-label">Email</label>
                        <input
                          className="amplify-input"
                          type="email"
                          value={username}
                          onChange={handleUsernameChange}
                          disabled={isSubmitting}
                          required
                          placeholder="Enter your Email"
                        />
                      </div>
                      <div className="amplify-flex amplify-field amplify-textfield amplify-passwordfield position-relative">
                        <label className="amplify-label">Password</label>
                        <input
                          className="amplify-input"
                          type={passwordVisible ? "text" : "password"}
                          value={password}
                          onChange={handlePasswordChange}
                          disabled={isSubmitting}
                          required
                          placeholder="Enter your Password"
                        />
                        <button
                          type="button"
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          className={styles["togglePasswordVisibleButton"]}
                        >
                          {passwordVisible ? <EyeIconSvg /> : <EyeIconDisabledSvg />}
                        </button>
                      </div>
                    </fieldset>

                    <button
                      type="submit"
                      className="amplify-button amplify-button--primary amplify-button--fullwidth"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Signing in..." : "Sign in"}
                    </button>

                    <ErrorDisplay error={error} onDismiss={() => setError("")} />
                    <div data-amplify-footer="">
                      <button
                        type="button"
                        className="amplify-button amplify-field-group__control amplify-button--link amplify-button--small"
                        disabled={isSubmitting}
                        onClick={handleForgotPassword}
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </RegisterPageOutline>
    );
  }

  return (
    <>
      <DashboardHeader showResaleCart={false} />
      <div data-amplify-authenticator="">
        <div data-amplify-container="">
          <div data-amplify-router="">
            <div className={styles["confirmation_form_wrapper"]}>
              <form
                onSubmit={handleConfirmSignIn}
                data-amplify-form=""
                className={styles["confirmation_form_container"]}
              >
                <h4 className="amplify-heading amplify-heading--4">We sent a code to you</h4>
                <p className="amplify-text amplify-authenticator__subtitle">
                  Your code is on the way to your email or mobile phone. It may take a minute to arrive. If you don’t
                  receive your code, please contact Member Services on 1800 066 844.
                </p>

                <div className="amplify-flex amplify-field d-flex">
                  <label className="amplify-label">Please enter the code below</label>
                  <input
                    className={classNames(
                      window.navigator.userAgent.includes("Windows") && styles["input-code"],
                      "amplify-input amplify-field-group__control gap-x-2",
                    )}
                    name="confirmation_code"
                    type="text"
                    value={otp}
                    onChange={handleOtpChange}
                    placeholder=""
                    maxLength={6}
                    disabled={isSubmitting}
                    required
                  />
                  <button
                    type="button"
                    className={classNames(styles["resend_button"], "amplify-button amplify-field-group__control")}
                    onClick={handleResendCode}
                    disabled={isSubmitting}
                  >
                    Resend code
                  </button>
                  <ErrorDisplay error={error} onDismiss={() => setError("")} />
                </div>

                <button
                  type="submit"
                  className="amplify-button amplify-button--primary amplify-button--fullwidth"
                  disabled={isSubmitting || otp.length !== 6}
                >
                  {isSubmitting ? "Verifying..." : "Confirm"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <DashboardFooter />
    </>
  );
};
